import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { DictionaryWord } from "@-/dev.improve.haniverse.lib.morpheme.word.read";
import {
  SENTENCE_EXAMPLES_LIMIT,
  useSentenceExamplesQueryOptions,
  type SentenceExample,
} from "@-/dev.improve.haniverse.lib.sentence";
import { WordRow } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.word-row";
import { SentenceRow } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.sentences._components.sentence-row";

const INITIAL_SENTENCE_COUNT = 2;
const SENTENCE_COUNT_STEP = 2;

function UsageWordSentence({
  onRevealMore,
  sentence,
  showTopDivider,
  word,
}: {
  onRevealMore: () => void;
  sentence: SentenceExample;
  showTopDivider: boolean;
  word: string;
}) {
  const stateKey = `usage-word:${word}:sentence:${sentence.id}`;
  const [expanded, setExpanded] = useRouteBodyState(`${stateKey}:expanded`, false);
  const [hasRevealedMore, setHasRevealedMore] = useRouteBodyState(
    `${stateKey}:revealed-more`,
    false,
  );

  return (
    <SentenceRow
      expanded={expanded}
      onToggleExpanded={() => {
        if (!expanded && !hasRevealedMore) {
          setHasRevealedMore(true);
          onRevealMore();
        }
        setExpanded(!expanded);
      }}
      sentence={sentence}
      showTopDivider={showTopDivider}
      underlinedWord={word}
    />
  );
}

/** A word's examples grow by two each time a sentence is first expanded. */
export function UsageWordSurface({ word }: { word: DictionaryWord }) {
  const [visibleCount, setVisibleCount] = useRouteBodyState(
    `usage-word:${word.word}:visible-sentence-count`,
    INITIAL_SENTENCE_COUNT,
  );
  const {
    data: sentences,
    error,
    isFetching,
    isLoading,
  } = useQuery({
    ...useSentenceExamplesQueryOptions(
      word.word,
      Math.max(SENTENCE_EXAMPLES_LIMIT, visibleCount + SENTENCE_COUNT_STEP + 1),
    ),
    // Keep expanded rows mounted while the next batch is read.
    placeholderData: (previous) => previous,
  });

  let body;
  if (isLoading) {
    body = <Text className="px-3 py-2 text-xs text-muted-foreground">Loading examples…</Text>;
  } else if (error && !sentences?.length) {
    body = (
      <Text className="px-3 py-2 text-xs text-destructive">Failed to load sentence examples.</Text>
    );
  } else if (!sentences?.length) {
    body = (
      <Text className="px-3 py-2 text-xs text-muted-foreground">
        No sentence examples for {word.word} yet.
      </Text>
    );
  } else {
    body = sentences
      .slice(0, visibleCount)
      .map((sentence, index) => (
        <UsageWordSentence
          key={sentence.id}
          onRevealMore={() => setVisibleCount((count) => count + SENTENCE_COUNT_STEP)}
          sentence={sentence}
          showTopDivider={index > 0}
          word={word.word}
        />
      ));
  }

  const allExamplesShown =
    !error && !isFetching && !!sentences?.length && visibleCount >= sentences.length;

  return (
    <WordRow
      footerCenter={
        error && sentences?.length ? (
          <Text className="text-xs text-destructive">Couldn’t load more examples.</Text>
        ) : allExamplesShown ? (
          <Text className="text-xs text-muted-foreground">All examples shown</Text>
        ) : isFetching && sentences?.length ? (
          <Text className="text-xs text-muted-foreground">Loading examples…</Text>
        ) : null
      }
      word={word}
    >
      {body}
    </WordRow>
  );
}
